import i18n from '../i18n';
import { formatDateTime, formatDate } from './date';
import { buildSessionDateTimeParts } from './sessionDateTimeDisplay';
import { formatCurrency } from './currency';

export function walletTxnAmount(amount) {
  const n = Number(amount ?? 0);
  return Number.isFinite(n) ? n : 0;
}

export function compareWalletTxns(a, b) {
  const dateA = new Date(a?.created ?? 0).getTime();
  const dateB = new Date(b?.created ?? 0).getTime();
  if (dateA !== dateB) return dateA - dateB;
  return walletTxnAmount(a?.txnId) - walletTxnAmount(b?.txnId);
}

export function computeRunningBalances(transactions, currentBalance = 0) {
  const sorted = [...(transactions || [])].sort(compareWalletTxns);
  let initialBalance = walletTxnAmount(currentBalance);
  sorted.forEach((t) => {
    const amount = walletTxnAmount(t.amount);
    if (t.txnType === 'CREDIT') {
      initialBalance -= amount;
    } else {
      initialBalance += amount;
    }
  });

  let runningBalance = initialBalance;
  return sorted.map((t) => {
    const amount = walletTxnAmount(t.amount);
    if (t.txnType === 'CREDIT') {
      runningBalance += amount;
    } else {
      runningBalance -= amount;
    }
    return { ...t, runningBalance };
  });
}

export function hasSessionContext(txn) {
  return txn?.sessionId != null || txn?.sessionDate != null;
}

export function isWalletTopupTxn(txn) {
  const base = (txn?.reason || '').split(':')[0].split('[')[0].trim();
  return base === 'ADVANCE_PAYMENT' || base === 'ONLINE_TOPUP';
}

export function txnPaidOnLabel(txn) {
  if (!isWalletTopupTxn(txn) || !txn?.paidOn) return null;
  return formatDate(txn.paidOn);
}

export function getWalletTxnDateLines(txn) {
  const lines = [];

  if (hasSessionContext(txn) && txn.sessionDate) {
    const parts = buildSessionDateTimeParts(txn.sessionDate, txn.sessionEndTime);
    const value = parts
      ? [parts.date, parts.timeRange].filter(Boolean).join(' · ')
      : formatDateTime(txn.sessionDate);
    lines.push({ key: 'session', label: i18n.t('wallet.dateLines.session'), value, tone: 'session' });
  }

  lines.push({
    key: 'created',
    label: i18n.t('wallet.dateLines.created'),
    value: txn?.created ? formatDateTime(txn.created) : '—',
    tone: 'created',
  });

  const paidOn = txnPaidOnLabel(txn);
  if (paidOn) {
    lines.push({ key: 'paidOn', label: i18n.t('wallet.dateLines.paidOn'), value: paidOn, tone: 'paidOn' });
  }

  return lines;
}

export function walletTxnWhenLabel(txn) {
  const lines = getWalletTxnDateLines(txn);
  if (!lines.length) return '—';
  return lines.map((line) => `${line.label}: ${line.value}`).join(' · ');
}

export function txnLabel(reason) {
  if (!reason) return i18n.t('wallet.txnReasons.default');
  const base = reason.split(':')[0].split('[')[0].trim();
  const key = `wallet.txnReasons.${base}`;
  if (i18n.exists(key)) return i18n.t(key);
  const pretty = base.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
  return i18n.t('wallet.txnReasons.unknown', { reason: pretty, defaultValue: pretty });
}

export function formatWalletAmount(amount) {
  return formatCurrency(walletTxnAmount(amount));
}
